import {
  CSSProperties,
  memo,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import peoplyLandingHtml from '@/assets/peoply-landing-roadmap-v9.dc.html?raw';
import peoplyLandingMobileHtml from '@/assets/peoply-landing-mobile-v9.dc.html?raw';
import peoplyLandingTabletHtml from '@/assets/peoply-landing-tablet-v9.dc.html?raw';
import { SeoMeta } from '@/components/SeoMeta';
import { LandingCta } from '@/components/LandingCta';
import { DemoRequestDialog } from '@/components/DemoRequestDialog';
import { LandingCompactMenu } from '@/components/LandingCompactMenu';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  LANDING_COMPACT_ACTION_ATTR,
  LANDING_COMPACT_ACTIONS,
  LANDING_COMPACT_EXTRA_TEXT_KEYS,
  LANDING_COMPACT_LANG_MOUNT_ID,
  LANDING_COMPACT_LAYOUTS,
  LANDING_COMPACT_MENU_MOUNT_ID,
} from '@/constants/landingCompact';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { createPortal } from 'react-dom';
import {
  LANDING_ROADMAP_ACTIONS,
  LANDING_ROADMAP_FIT_HEIGHT_ATTR,
  LANDING_ROADMAP_FIT_LINES_ATTR,
  LANDING_ROADMAP_MODULE_ATTR,
  LANDING_ROADMAP_ZOOM_VAR,
} from '@/constants/landingRoadmap';
import { MODULE_PAGES } from '@/constants/modulePages';
import { getModulePagePath } from '@/lib/modulePages';
import type { ModulePageSlug } from '@/types/modulePages';
import { LANDING_ROADMAP_TEXT_KEYS } from '@/constants/landingRoadmapText';
import {
  advanceLandingRoadmapPlayback,
  computeLandingRoadmapValues,
  fitLandingRoadmapContent,
  getLandingRoadmapPlaybackTarget,
  getLandingRoadmapZoom,
  measureLandingRoadmapScroll,
  resolveLandingRoadmapTemplate,
} from '@/lib/landingRoadmap';
import type {
  LandingCompactLayout,
  LandingRoadmapAction,
  LandingRoadmapPlayback,
  LandingRoadmapScrollState,
} from '@/types/landingRoadmap';

const BOUND_ATTR_PREFIX = 'data-lr-tpl-';
const NAV_ACTIONS_ID = 'peoply-landing-nav-actions';
// Artboard'un doğrudan çocuğu olan SVG'ler (hero çizgisi, yol haritası) ve hero'daki animasyonlu daire (tam boy
// katmanlar) dekoratiftir; sayfanın sol üstünden başlayıp hero butonlarını örterler, tıklamaları alttaki öğelere geçirmeleri gerekir.
const landingStyles = `${peoplyLandingHtml.match(/<style>([\s\S]*?)<\/style>/)?.[1] ?? ''}.r>svg,.r>div[style*="inset:0;transform-origin"]{pointer-events:none}`;
const landingFontsHref = (peoplyLandingHtml.match(/<link rel="stylesheet" href="([^"]+)"/)?.[1] ?? '').replaceAll(
  '&amp;',
  '&'
);
const staticLandingValues = computeLandingRoadmapValues({ mode: 'static' });

// {{x}} içeren her niteliği başlangıç (statik) değeriyle çözer ve şablonu data-lr-tpl-<nitelik>'te saklar;
// scroll sırasında yalnızca bu nitelikler yeniden hesaplanır.
const landingMarkup = (peoplyLandingHtml.match(/<\/helmet>\s*([\s\S]*?)<\/x-dc>/)?.[1] ?? '')
  .replace(
    /\s([a-zA-Z-]+)="([^"]*\{\{[^"]*)"/g,
    (_, attr: string, template: string) =>
      ` ${attr}="${resolveLandingRoadmapTemplate(template, staticLandingValues)}" ${BOUND_ATTR_PREFIX}${attr}="${template}" data-lr-bound`
  )
  .replace(/<img\b/g, '<img decoding="async"')
  // Mega menüdeki modül satırları sırasıyla modül sayfalarına bağlanır.
  .replace(
    /class="mi"/g,
    (
      (index) => (match: string) => {
        const module = MODULE_PAGES[index++];
        return module ? `${match} ${LANDING_ROADMAP_MODULE_ATTR}="${module.slug}"` : match;
      }
    )(0)
  )
  .replace(
    '<div style="position:absolute;right:48px;top:24px;display:flex;align-items:center;gap:26px;z-index:56"',
    `<div id="${NAV_ACTIONS_ID}" style="position:absolute;right:48px;top:24px;display:flex;align-items:center;gap:26px;z-index:56"`
  )
  // Çeviriler uzun olduğunda taşmasın: hero başlığı 3 satıra, CV ve sınav kartları tasarımdaki yüksekliğe sığdırılır.
  .replace(
    '<h1 style="position:absolute;left:64px;top:162px;',
    `<h1 ${LANDING_ROADMAP_FIT_LINES_ATTR}="3" style="position:absolute;left:64px;top:162px;`
  )
  .replaceAll(
    '<div style="position:relative;width:520px;box-sizing:border-box;background:#FBFCFF;',
    `<div ${LANDING_ROADMAP_FIT_HEIGHT_ATTR}="806" style="position:relative;width:520px;box-sizing:border-box;background:#FBFCFF;`
  )
  // Artboard zoom ile küçültüldüğünde vh de zoom'lanır; sticky kartlar ekranın ortasında kalsın diye telafi edilir.
  .replaceAll('calc(50vh - ', `calc(50vh / var(${LANDING_ROADMAP_ZOOM_VAR}, 1) - `)
  .trim();

// Mobil (Mobil V9, 390px) ve tablet (Tablet V9, 834px) tasarımları statiktir; scroll bağı yoktur, animasyonları
// kendi CSS'lerindedir.
function parseCompactLanding(html: string) {
  return {
    styles: html.match(/<style>([\s\S]*?)<\/style>/)?.[1] ?? '',
    markup: (html.match(/<\/helmet>\s*([\s\S]*?)<\/x-dc>/)?.[1] ?? '')
      .replace(/<img\b/g, '<img decoding="async"')
      .trim(),
  };
}

const compactLandings: Record<LandingCompactLayout, { styles: string; markup: string }> = {
  mobile: parseCompactLanding(peoplyLandingMobileHtml),
  tablet: parseCompactLanding(peoplyLandingTabletHtml),
};

// Aktif tasarımın artboard'u ekran genişliğine ölçeklenir: mobil/tablet büyüyebilir (üst sınırlı), masaüstü
// (1280px) yalnızca küçülür.
function getLandingArtboardZoom(layout: LandingCompactLayout | null) {
  if (!layout) return getLandingRoadmapZoom();
  const { artboardWidth, maxZoom } = LANDING_COMPACT_LAYOUTS[layout];
  return Math.min(document.documentElement.clientWidth / artboardWidth, maxZoom);
}

function normalizeText(value: string | null | undefined) {
  return value?.replace(/\s+/g, ' ').trim() ?? '';
}

function escapeHtml(value: string) {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// Markup'taki her metin düğümünü kaynak (Türkçe) metnine göre aktif dildeki karşılığıyla değiştirir.
function localizeLandingMarkup(markup: string, translations: Record<string, string>) {
  return markup.replace(/>([^<>]+)</g, (match, text: string) => {
    const normalized = normalizeText(text.replaceAll('&nbsp;', ' ').replaceAll('&amp;', '&'));
    const translation = translations[normalized];
    if (!translation) return match;
    const leading = text.match(/^\s*/)?.[0] ?? '';
    const trailing = text.match(/\s*$/)?.[0] ?? '';
    return `>${leading}${escapeHtml(translation)}${trailing}<`;
  });
}

const LandingMarkupView = memo(function LandingMarkupView({
  rootRef,
  markup,
  styles,
  contentStyle,
  children,
}: {
  rootRef: RefObject<HTMLDivElement | null>;
  markup: string;
  styles: string;
  contentStyle?: CSSProperties;
  children?: ReactNode;
}) {
  // Yeni bir { __html } objesi React'in markup'ı baştan yazmasına yol açar; bu da scroll bağlarını ve dil seçici
  // bağlama noktasını DOM'dan koparır. Obje yalnızca markup değişince yenilenir.
  const html = useMemo(() => ({ __html: markup }), [markup]);

  return (
    <div
      ref={rootRef}
      className='peoply-landing-page'
      style={{ position: 'relative', isolation: 'isolate', background: '#F3F1EC', minHeight: '100vh' }}
    >
      <style>{styles}</style>
      {/* Tasarımın kâğıt dokusu artboard yerine tüm genişliğe uygulanır; yanlarla gövde aynı tonda kalır. */}
      <svg
        aria-hidden='true'
        width='100%'
        height='100%'
        style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.1,
          mixBlendMode: 'multiply',
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <filter id='peoply-landing-grain'>
          <feTurbulence type='fractalNoise' baseFrequency='.85' numOctaves={2} />
        </filter>
        <rect width='100%' height='100%' filter='url(#peoply-landing-grain)' />
      </svg>
      <div style={contentStyle} dangerouslySetInnerHTML={html} />
      {children}
    </div>
  );
});

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const rootRef = useRef<HTMLDivElement>(null);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const openDemoModal = useCallback(() => setIsDemoModalOpen(true), []);
  const currentLanguage = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0];
  const isMobile = useMediaQuery(LANDING_COMPACT_LAYOUTS.mobile.mediaQuery);
  const isTablet = useMediaQuery(LANDING_COMPACT_LAYOUTS.tablet.mediaQuery);
  // null: masaüstü (Roadmap v9) tasarımı.
  const compactLayout: LandingCompactLayout | null = isMobile ? 'mobile' : isTablet ? 'tablet' : null;
  const translatedMarkup = useMemo(
    () => {
      const translations = Object.fromEntries(
        Object.entries(LANDING_ROADMAP_TEXT_KEYS).map(([source, key]) => [source, t(`landingPage.roadmap.${key}`)])
      );
      if (!compactLayout) return localizeLandingMarkup(landingMarkup, translations);
      return localizeLandingMarkup(compactLandings[compactLayout].markup, {
        ...translations,
        ...Object.fromEntries(Object.entries(LANDING_COMPACT_EXTRA_TEXT_KEYS).map(([source, key]) => [source, t(key)])),
      });
    },
    // t, aktif dil değişince yeni referans döner; resolvedLanguage ayrıca açıkça bağımlılık.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.resolvedLanguage, compactLayout]
  );
  const [artboardZoom, setArtboardZoom] = useState(() => getLandingArtboardZoom(compactLayout));
  const artboardContentStyle = useMemo<CSSProperties | undefined>(
    () =>
      compactLayout || artboardZoom < 1
        ? ({ zoom: artboardZoom, [LANDING_ROADMAP_ZOOM_VAR]: artboardZoom } as CSSProperties)
        : undefined,
    [compactLayout, artboardZoom]
  );
  const seoTitle = t('landingPage.seo.title');
  const seoDescription = t('landingPage.seo.description');
  const seoKeywords = t('landingPage.seo.keywords');
  const alternateLanguages = useMemo(
    () => [
      { hrefLang: 'tr', href: 'https://peoply.ai/' },
      { hrefLang: 'en', href: 'https://peoply.ai/?lng=en' },
      { hrefLang: 'x-default', href: 'https://peoply.ai/' },
    ],
    []
  );
  const structuredData = useMemo(
    () => [
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Peoply',
        url: 'https://peoply.ai/',
        logo: 'https://peoply.ai/peoply-logo.svg',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Peoply',
        url: 'https://peoply.ai/',
        inLanguage: currentLanguage === 'en' ? 'en-US' : 'tr-TR',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Peoply',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: 'https://peoply.ai/',
        description: seoDescription,
        offers: [
          {
            '@type': 'Offer',
            price: '5',
            priceCurrency: 'USD',
            category: t('landingPage.structuredData.offers.starter'),
          },
          {
            '@type': 'Offer',
            price: '25',
            priceCurrency: 'USD',
            category: t('landingPage.structuredData.offers.pro'),
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [0, 1, 2, 3].map((index) => ({
          '@type': 'Question',
          name: t(`publicPages.home.faq.items.${index}.question`),
          acceptedAnswer: {
            '@type': 'Answer',
            text: t(`publicPages.home.faq.items.${index}.answer`),
          },
        })),
      },
    ],
    [currentLanguage, seoDescription, t]
  );

  useEffect(() => {
    if (!landingFontsHref) return;

    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href = landingFontsHref;
    document.head.appendChild(fontLink);

    return () => {
      fontLink.remove();
    };
  }, []);

  useEffect(() => {
    const previousLang = document.documentElement.lang;
    document.documentElement.lang = currentLanguage;

    return () => {
      document.documentElement.lang = previousLang;
    };
  }, [currentLanguage]);

  // Tasarımdaki scroll'a bağlı animasyonlar: bağlı nitelikleri ilk boyamadan önce ölçüp günceller.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || compactLayout) return;

    const bindings = Array.from(root.querySelectorAll<HTMLElement | SVGElement>('[data-lr-bound]')).flatMap((el) =>
      Array.from(el.attributes)
        .filter((attribute) => attribute.name.startsWith(BOUND_ATTR_PREFIX))
        .map((attribute) => ({
          el,
          attr: attribute.name.slice(BOUND_ATTR_PREFIX.length),
          template: attribute.value,
        }))
    );

    const render = (scroll: LandingRoadmapScrollState, playback: LandingRoadmapPlayback) => {
      const values = computeLandingRoadmapValues(scroll, playback);
      bindings.forEach(({ el, attr, template }) => {
        const value = resolveLandingRoadmapTemplate(template, values);
        if (el.getAttribute(attr) !== value) el.setAttribute(attr, value);
      });
    };

    // İlk ölçümde (sayfa ortasında yenileme dahil) animasyon olmadan hedef duruma geçilir; sonrasında
    // gösterilen durum hedefe hız sınırıyla yaklaşır, hızlı scroll'da adımlar sırayla oynar.
    const initialScroll = measureLandingRoadmapScroll();
    let playback = getLandingRoadmapPlaybackTarget(initialScroll);
    let lastTime = performance.now();
    let frame = 0;

    const tick = (now: number) => {
      frame = 0;
      const scroll = measureLandingRoadmapScroll();
      const advanced = advanceLandingRoadmapPlayback(playback, getLandingRoadmapPlaybackTarget(scroll), now - lastTime);
      lastTime = now;
      playback = advanced.playback;
      render(scroll, playback);
      if (!advanced.done) frame = requestAnimationFrame(tick);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(tick);
    };

    render(initialScroll, playback);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [translatedMarkup, compactLayout]);

  const runRoadmapAction = useCallback(
    (action: LandingRoadmapAction) => {
      if (action.type === 'demo') setIsDemoModalOpen(true);
      else if (action.type === 'external') window.location.assign(action.href);
      else navigate(action.to);
    },
    [navigate]
  );

  useEffect(() => {
    const root = rootRef.current;
    if (!root || compactLayout) return;

    // Eylemler i18n anahtarıyla tanımlı; aktif dildeki metne çevrilip DOM'daki metinle eşlenir.
    const actionsByText = new Map<string, LandingRoadmapAction>(
      Object.entries(LANDING_ROADMAP_ACTIONS).map(([key, action]) => [
        normalizeText(t(`landingPage.roadmap.${key}`)),
        action,
      ])
    );
    const actionTargets = Array.from(root.querySelectorAll<HTMLElement>('span, button, div')).filter(
      (el) => actionsByText.has(normalizeText(el.textContent)) && !el.querySelector('span, button, div')
    );
    actionTargets.forEach((el) => {
      el.style.cursor = 'pointer';
    });

    const handleClick = (event: MouseEvent) => {
      const target = (event.target as Element | null)?.closest('span, button, div') ?? null;
      const el = actionTargets.find((candidate) => candidate === target || candidate.contains(target));
      const action = el && actionsByText.get(normalizeText(el.textContent));
      if (!action) return;

      event.preventDefault();
      runRoadmapAction(action);
    };

    root.addEventListener('click', handleClick);
    return () => root.removeEventListener('click', handleClick);
  }, [runRoadmapAction, t, translatedMarkup, compactLayout]);

  // Platform mega menüsündeki modül satırları ilgili modül tanıtım sayfasına gider.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || compactLayout) return;

    root.querySelectorAll<HTMLElement>(`[${LANDING_ROADMAP_MODULE_ATTR}]`).forEach((el) => {
      el.style.cursor = 'pointer';
    });

    const handleClick = (event: MouseEvent) => {
      const slug = (event.target as Element | null)
        ?.closest(`[${LANDING_ROADMAP_MODULE_ATTR}]`)
        ?.getAttribute(LANDING_ROADMAP_MODULE_ATTR) as ModulePageSlug | null | undefined;
      if (!slug) return;

      event.preventDefault();
      navigate(getModulePagePath(slug));
    };

    root.addEventListener('click', handleClick);
    return () => root.removeEventListener('click', handleClick);
  }, [navigate, translatedMarkup, compactLayout]);

  // Mobil/tablet tasarımlarında tıklanabilir öğeler markup'ta data-landing-action ile işaretlidir.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !compactLayout) return;

    root.querySelectorAll<HTMLElement>(`[${LANDING_COMPACT_ACTION_ATTR}]`).forEach((el) => {
      el.style.cursor = 'pointer';
    });

    const handleClick = (event: MouseEvent) => {
      const action = (event.target as Element | null)
        ?.closest(`[${LANDING_COMPACT_ACTION_ATTR}]`)
        ?.getAttribute(LANDING_COMPACT_ACTION_ATTR);
      if (!action) return;

      event.preventDefault();
      if (action === LANDING_COMPACT_ACTIONS.demo) setIsDemoModalOpen(true);
      else if (action === LANDING_COMPACT_ACTIONS.login) runRoadmapAction(LANDING_ROADMAP_ACTIONS.navLogin);
      else if (action === LANDING_COMPACT_ACTIONS.signup) runRoadmapAction(LANDING_ROADMAP_ACTIONS.navSignup);
    };

    root.addEventListener('click', handleClick);
    return () => root.removeEventListener('click', handleClick);
  }, [runRoadmapAction, translatedMarkup, compactLayout]);

  // Artboard ekran genişliğine ölçeklenir; daha dar ekranlarda yatay taşma/kırpılma olmaz.
  useLayoutEffect(() => {
    const updateZoom = () => setArtboardZoom(getLandingArtboardZoom(compactLayout));
    updateZoom();
    window.addEventListener('resize', updateZoom);
    return () => window.removeEventListener('resize', updateZoom);
  }, [compactLayout]);

  // Çevrilen metinleri tasarım kutularına sığdırır; ölçüler yazı tipine bağlı olduğundan fontlar yüklenince tekrarlanır.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || compactLayout) return;
    let cancelled = false;
    fitLandingRoadmapContent(root);
    void document.fonts.ready.then(() => {
      if (!cancelled) fitLandingRoadmapContent(root);
    });
    return () => {
      cancelled = true;
    };
  }, [translatedMarkup, compactLayout, artboardZoom]);

  // Dil seçici tasarımın üst menüsüne, "Giriş Yap"ın soluna yerleşir; markup her dil değişiminde yenilendiği için
  // bağlama noktası da yeniden oluşturulur.
  const [langSwitcherMountEl, setLangSwitcherMountEl] = useState<HTMLElement | null>(null);
  // Mobil/tablette dil seçici menü grubunun başına, hamburger menüsü tasarımdaki hamburger'in yerine yerleşir.
  const [compactMenuMountEl, setCompactMenuMountEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const root = rootRef.current;
    const navActions = root?.querySelector<HTMLElement>(
      `#${compactLayout ? LANDING_COMPACT_LANG_MOUNT_ID : NAV_ACTIONS_ID}`
    );
    if (!navActions) return;
    const mountEl = document.createElement('span');
    mountEl.style.display = 'contents';
    navActions.prepend(mountEl);
    setLangSwitcherMountEl(mountEl);
    setCompactMenuMountEl(
      compactLayout ? (root?.querySelector<HTMLElement>(`#${LANDING_COMPACT_MENU_MOUNT_ID}`) ?? null) : null
    );
    return () => {
      mountEl.remove();
      setLangSwitcherMountEl(null);
      setCompactMenuMountEl(null);
    };
  }, [translatedMarkup, compactLayout]);

  const handleCompactMenuSelect = useCallback(
    (key: keyof typeof LANDING_ROADMAP_ACTIONS) => runRoadmapAction(LANDING_ROADMAP_ACTIONS[key]),
    [runRoadmapAction]
  );

  return (
    <>
      <SeoMeta
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        path='/'
        imagePath='/opengraph-image.png'
        alternateLanguages={alternateLanguages}
        structuredData={structuredData}
      />
      <LandingMarkupView
        rootRef={rootRef}
        markup={translatedMarkup}
        styles={compactLayout ? compactLandings[compactLayout].styles : landingStyles}
        contentStyle={artboardContentStyle}
      >
        {/* Mobil/tablet tasarımlarının CTA bölümü kendi markup'larında */}
        {!compactLayout && <LandingCta onFindCandidatesClick={openDemoModal} />}
      </LandingMarkupView>
      {langSwitcherMountEl && createPortal(<LanguageSwitcher variant='landing' />, langSwitcherMountEl)}
      {compactMenuMountEl &&
        createPortal(<LandingCompactMenu onSelect={handleCompactMenuSelect} />, compactMenuMountEl)}

      <DemoRequestDialog open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen} />
    </>
  );
}
